import {
  typographySchema,
  updateTenantAppearanceRequestSchema,
  type Typography,
} from '@gb-ticket/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError, apiFetch } from '@/lib/api';
import { publicBrandQueryKey, usePublicBrand } from '@/platform/brand/use-public-brand';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ThemePreview } from '@/platform/features/profile/appearance-choices';
import {
  COLOR_MODE_OPTIONS,
  VARIANT_OPTIONS,
} from '@/platform/features/profile/appearance-options';
import { TypographyCard } from '@/platform/features/profile/typography-controls';
import { useOwnProfile } from '@/platform/features/users/use-own-profile';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { applyAppearance } from '@/platform/theme/apply-appearance';
import { resolveAppearance, type Appearance } from '@/platform/theme/appearance';
import { writeAppearance } from '@/platform/theme/appearance-storage';
import { useResolvedColorScheme } from '@/platform/theme/appearance-provider';
import {
  applyTypography,
  browserTypographyStorage,
  writeTypography,
} from '@/platform/theme/typography';

const SAVE_FAILED = 'Die Schrift konnte nicht gespeichert werden.';

export function TypographyAdminPage() {
  usePageTitle('Darstellung');
  const brandQuery = usePublicBrand();
  const profile = useOwnProfile();
  const queryClient = useQueryClient();
  const brand = brandQuery.data;

  return (
    <section className="w-full min-w-0 space-y-6">
      <PlatformPageHeader
        title="Darstellung"
        description="Standard für Erscheinungsbild, Schrift und Schriftgröße. Gilt für alle, die selbst nichts gewählt haben."
      />
      {brandQuery.isPending ? (
        <p>Darstellung wird geladen …</p>
      ) : brandQuery.isError || !brand ? (
        <p>Die Darstellung konnte nicht geladen werden.</p>
      ) : (
        <>
          <TenantAppearanceCard
            brand={{
              colorMode: brand.colorMode,
              designVariant: brand.designVariant,
            }}
            onSaved={(saved) => {
              queryClient.setQueryData(publicBrandQueryKey, {
                ...brand,
                colorMode: saved.colorMode,
                designVariant: saved.designVariant,
              });
              if (profile.isPending && !profile.data) {
                return;
              }
              const applied = resolveAppearance(profile.data, saved);
              writeAppearance(browserAppearanceStorage(), applied);
              applyAppearance(document.documentElement, applied, systemPrefersDark());
            }}
          />
          <TypographyCard
            key={`${brand.displayFont}:${brand.displayFontSize}`}
            mode="tenant"
            saved={{ displayFont: brand.displayFont, displayFontSize: brand.displayFontSize }}
            brand={{ displayFont: brand.displayFont, displayFontSize: brand.displayFontSize }}
            onSave={async (next) => {
              if (!next) {
                return;
              }
              try {
                const saved = await apiFetch('/typography', typographySchema, {
                  method: 'PATCH',
                  body: JSON.stringify(next),
                });
                queryClient.setQueryData(publicBrandQueryKey, {
                  ...brand,
                  displayFont: saved.displayFont,
                  displayFontSize: saved.displayFontSize,
                });
                const followsTenant = !profile.data?.displayFont || !profile.data.displayFontSize;
                if (followsTenant) {
                  const applied: Typography = {
                    displayFont: saved.displayFont,
                    displayFontSize: saved.displayFontSize,
                  };
                  writeTypography(browserTypographyStorage(), applied);
                  applyTypography(document.documentElement, applied);
                }
                toast.success('Schrift gespeichert.');
              } catch (error) {
                toast.error(error instanceof ApiRequestError ? error.message : SAVE_FAILED);
              }
            }}
          />
        </>
      )}
    </section>
  );
}

function browserAppearanceStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

function TenantAppearanceCard({
  brand,
  onSaved,
}: {
  brand: Appearance;
  onSaved: (saved: Appearance) => void;
}) {
  const copy = useVoiceCopy();
  const resolved = useResolvedColorScheme();
  const [appearance, setAppearance] = useState<Appearance>(brand);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  async function commit(next: Appearance) {
    const previous = appearance;
    setSaving(true);
    setAppearance(next);
    setStatus('Wird gespeichert.');
    try {
      const saved = await apiFetch('/typography/appearance', updateTenantAppearanceRequestSchema, {
        method: 'PATCH',
        body: JSON.stringify(next),
      });
      onSaved(saved);
      setStatus('Übernommen.');
    } catch (error) {
      setAppearance(previous);
      setStatus(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Erscheinungsbild konnte nicht gespeichert werden.',
      );
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Erscheinungsbild konnte nicht gespeichert werden.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Erscheinungsbild</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <fieldset className="space-y-3" disabled={saving}>
          <legend className="text-sm font-medium">Farbmodus</legend>
          <p className="text-sm text-muted-foreground">
            {copy('platform.profile.appearance.system')}
          </p>
          <div className="flex flex-col gap-2">
            {COLOR_MODE_OPTIONS.map((option) => {
              const checked = appearance.colorMode === option.value;
              return (
                <label
                  key={option.value}
                  className="flex min-w-0 cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2"
                >
                  <input
                    type="radio"
                    name="tenant-color-mode"
                    value={option.value}
                    checked={checked}
                    onChange={() => {
                      void commit({ ...appearance, colorMode: option.value });
                    }}
                  />
                  <span className="min-w-0 text-sm">{option.label}</span>
                  {checked ? <span className="ml-auto text-sm font-medium">Aktiv</span> : null}
                </label>
              );
            })}
          </div>
        </fieldset>
        <fieldset className="space-y-3" disabled={saving}>
          <legend className="text-sm font-medium">Designvariante</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {VARIANT_OPTIONS.map((option) => {
              const checked = appearance.designVariant === option.value;
              return (
                <label
                  key={option.value}
                  className="flex min-w-0 cursor-pointer flex-col gap-3 rounded-md border border-border p-3"
                >
                  <input
                    type="radio"
                    name="tenant-design-variant"
                    value={option.value}
                    checked={checked}
                    onChange={() => {
                      void commit({ ...appearance, designVariant: option.value });
                    }}
                  />
                  <ThemePreview variant={option.value} dark={resolved === 'dark'} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {option.description}
                    </span>
                  </span>
                  {checked ? <span className="text-sm font-medium">Aktiv</span> : null}
                </label>
              );
            })}
          </div>
        </fieldset>
        <p className="text-sm text-muted-foreground">{status}</p>
      </CardContent>
    </Card>
  );
}
